import { Link } from "react-router-dom";
import { Bed, Bath, Wifi, Wind, Coffee, Users, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const rooms = [
  {
    id: "wave-rider",
    name: "THE WAVE RIDER",
    tagline: "For the free spirits",
    price: 149,
    img: "https://images.unsplash.com/photo-1763616310394-b8ed0a3318c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb2xvcmZ1bCUyMGJlZHJvb20lMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NzIxMjUyOTF8MA&ixlib=rb-4.1.0&q=85",
    description: "Ocean-facing queen room with hand-painted surf murals, a private balcony, and enough natural light to make any Instagram influencer weep. The Wave Rider is for those who want to fall asleep to the sound of crashing waves.",
    amenities: ["Queen Bed", "Private Balcony", "Ocean View", "En-suite Bath", "Free WiFi", "AC"],
    guests: 2,
    size: "320 sq ft",
  },
  {
    id: "sandcastle",
    name: "THE SANDCASTLE",
    tagline: "For the dreamers",
    price: 189,
    img: "https://images.unsplash.com/photo-1687941544146-351d41b57cbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb2xvcmZ1bCUyMGJlZHJvb20lMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NzIxMjUyOTF8MA&ixlib=rb-4.1.0&q=85",
    description: "Our most popular room. King bed with a built-in smart display and ambient lighting you control from your phone. The bathroom has a rain shower that'll make you forget what a hurry feels like. Tropical touches everywhere.",
    amenities: ["King Bed", "Smart Display", "Rain Shower", "Smart TV", "Free WiFi", "AC"],
    guests: 2,
    size: "380 sq ft",
  },
  {
    id: "lighthouse",
    name: "THE LIGHTHOUSE",
    tagline: "For the romantics",
    price: 229,
    img: "https://images.pexels.com/photos/3754595/pexels-photo-3754595.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    description: "Our premium suite with panoramic beach views, a soaking tub, and a curated vinyl collection with Bluetooth throughout. The Lighthouse is where luxury meets laid-back. Perfect for honeymoons, anniversaries, or just treating yourself.",
    amenities: ["King Bed", "Soaking Tub", "Panoramic View", "Bluetooth Audio", "Free WiFi", "AC"],
    guests: 2,
    size: "450 sq ft",
  },
  {
    id: "tide-pool",
    name: "THE TIDE POOL",
    tagline: "For the families",
    price: 199,
    img: "https://images.unsplash.com/photo-1721076748177-50499ff00fac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHx0cm9waWNhbCUyMGJlZCUyMGJyZWFrZmFzdCUyMGNvenklMjByb29tfGVufDB8fHx8MTc3MjEyNTQzM3ww&ixlib=rb-4.1.0&q=85",
    description: "Spacious family room with a queen bed and twin bunk beds. Kid-friendly tech on the walls, games in the closet, and enough space to build a blanket fort. Adults welcome too.",
    amenities: ["Queen + Bunks", "Gaming Setup", "Garden View", "Full Bath", "Free WiFi", "AC"],
    guests: 4,
    size: "420 sq ft",
  },
];

const amenityIcons = {
  "Queen Bed": Bed, "King Bed": Bed, "Queen + Bunks": Bed,
  "Private Balcony": Wind, "Smart Display": Wifi, "Soaking Tub": Bath,
  "Rain Shower": Bath, "Full Bath": Bath, "En-suite Bath": Bath,
  "Free WiFi": Wifi, "AC": Wind, "Ocean View": Wind,
  "Smart TV": Wifi, "Panoramic View": Wind, "Bluetooth Audio": Coffee,
  "Gaming Setup": Coffee, "Garden View": Wind,
};

export default function RoomsPage() {
  return (
    <div data-testid="rooms-page">
      {/* Hero */}
      <section className="relative bg-ink py-20">
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <p className="font-marker text-teal text-sm">Where you'll crash</p>
          <h1 className="mt-2 font-anton text-4xl tracking-wider text-sand stencil-text sm:text-6xl">
            ROOMS & RATES
          </h1>
          <div className="mt-2 h-1 w-20 bg-coral" />
          <p className="mt-4 font-courier text-sm text-sand/70 max-w-lg">
            Four rooms. Four vibes. All include breakfast, WiFi, and
            a healthy dose of character. Prices per night.
          </p>
        </div>
      </section>

      {/* Rooms List */}
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-7xl px-6 space-y-16">
          {rooms.map((room, i) => (
            <div
              key={room.id}
              data-testid={`room-card-${room.id}`}
              className="card-brutal bg-white overflow-hidden"
            >
              <div className={`grid lg:grid-cols-2 ${i % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                <div className={`aspect-[4/3] lg:aspect-auto overflow-hidden ${i % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <img
                    src={room.img}
                    alt={room.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  {/* Classified stamp */}
                  <div className="classified-stamp text-xs mb-4">
                    {room.tagline}
                  </div>

                  <h2 className="font-anton text-2xl tracking-wider text-ink sm:text-3xl">
                    {room.name}
                  </h2>

                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-anton text-3xl text-teal">${room.price}</span>
                    <span className="font-courier text-xs text-ink/50">/ night</span>
                  </div>

                  <p className="mt-4 font-courier text-sm leading-relaxed text-ink/80">
                    {room.description}
                  </p>

                  <Separator className="my-6 bg-ink/10" />

                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-1 text-ink/60">
                      <Users className="h-4 w-4" />
                      <span className="font-outfit text-xs">Up to {room.guests}</span>
                    </div>
                    <span className="font-outfit text-xs text-ink/60">{room.size}</span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {room.amenities.map((a) => {
                      const Icon = amenityIcons[a] || Wifi;
                      return (
                        <span
                          key={a}
                          className="inline-flex items-center gap-1 border border-ink/20 px-3 py-1 font-outfit text-xs text-ink/70"
                        >
                          <Icon className="h-3 w-3" />
                          {a}
                        </span>
                      );
                    })}
                  </div>

                  <Link to="/contact" className="mt-8 inline-block">
                    <Button
                      data-testid={`room-book-${room.id}`}
                      className="bg-ink text-sand font-anton tracking-widest text-sm px-8 py-5 hover:-translate-y-1 hover:shadow-brutal-teal transition-all duration-200 rounded-none"
                    >
                      BOOK THIS ROOM <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Included */}
      <section className="bg-ink py-16">
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <h2 className="font-anton text-2xl tracking-wider text-sand stencil-text sm:text-3xl">
            ALWAYS INCLUDED
          </h2>
          <div className="mt-2 h-1 w-20 bg-teal" />

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Coffee, label: "Fresh Breakfast" },
              { icon: Wifi, label: "High-Speed WiFi" },
              { icon: Bath, label: "Premium Toiletries" },
              { icon: Wind, label: "Beach Towels & Gear" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 border border-sand/20 p-4">
                <item.icon className="h-6 w-6 text-teal flex-shrink-0" />
                <span className="font-outfit text-sm text-sand">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
